import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCategories } from '../../Contexts/CategoryContext';

/* ================= ANIMATION STYLES ================= */
const categoryAnimationStyles = `
  /* ── Staggered card entrance ── */
  .cat-card {
    opacity: 0;
    transform: translateY(32px);
    transition:
      opacity 0.65s cubic-bezier(0.16, 1, 0.3, 1),
      transform 0.65s cubic-bezier(0.16, 1, 0.3, 1),
      box-shadow 0.35s ease;
  }
  .cat-card.cat-card--visible {
    opacity: 1;
    transform: translateY(0);
  }

  /* Stagger delays per card index */
  .cat-card:nth-child(1) { transition-delay: 0ms; }
  .cat-card:nth-child(2) { transition-delay: 120ms; }
  .cat-card:nth-child(3) { transition-delay: 240ms; }
  .cat-card:nth-child(4) { transition-delay: 360ms; }
  .cat-card:nth-child(5) { transition-delay: 480ms; }
  .cat-card:nth-child(6) { transition-delay: 600ms; }

  /* ── Card stacking ── */
  .cat-card {
    position: relative;
    z-index: 1;
    isolation: isolate;
  }

  /* Ensure the block1 wrapper clips everything inside */
  .cat-card .block1 {
    overflow: hidden;
    isolation: isolate;
  }

  /* Constrain the hover overlay strictly within block1 bounds */
  .cat-card .block1-txt {
    overflow: hidden;
  }

  /* ── Image zoom on card hover ── */
  .cat-img-wrap {
    overflow: hidden;
    position: relative;
    width: 100%;
    padding-bottom: 66.67%;
    background-color: #f8f9fa;
  }

  .cat-img-wrap img {
    transition: transform 0.75s cubic-bezier(0.16, 1, 0.3, 1);
  }

  .cat-card:hover .cat-img-wrap img {
    transform: scale(1.08);
  }

  /* ── Overlay shimmer on hover ── */
  .cat-overlay-shimmer {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      135deg,
      rgba(113, 127, 224, 0) 0%,
      rgba(113, 127, 224, 0.12) 50%,
      rgba(113, 127, 224, 0) 100%
    );
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
    z-index: 1;
  }

  .cat-card:hover .cat-overlay-shimmer {
    opacity: 1;
  }

  /* ── "Shop Now" link animated underline ── */
  .block1-link {
    position: relative;
    display: inline-block;
  }
  .block1-link::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 0;
    height: 1px;
    background: #fff;
    transition: width 0.3s ease 0.1s;
  }
  .block1-txt:hover .block1-link::after {
    width: 100%;
  }

  /* ── Category name subtle slide-up on hover ── */
  .block1-name {
    transition: transform 0.35s cubic-bezier(0.16, 1, 0.3, 1),
                color 0.3s ease !important;
  }
  .block1-txt:hover .block1-name {
    transform: translateY(-3px);
  }

  /* ── MOBILE: disable entrance animation so cards are never stuck invisible ── */
  @media (max-width: 768px) {
    .cat-card {
      opacity: 1 !important;
      transform: none !important;
      transition: box-shadow 0.35s ease !important;
    }

    /* Tighter overlay padding on small screens */
    .cat-card .block1-txt.respon3 {
      padding-left: 16px !important;
      padding-right: 16px !important;
      padding-top: 16px !important;
      padding-bottom: 16px !important;
    }

    /* Scale down the category name on mobile */
    .cat-card .block1-name {
      font-size: 16px !important;
    }

    /* Scale down Shop Now text on mobile */
    .cat-card .block1-link {
      font-size: 11px !important;
    }
  }
`;

/* ================= MOBILE COLUMN STYLE ================= */
// Inline style applied directly on the column div — bypasses all Bootstrap
// and theme CSS specificity issues that prevent col-sm-6 from working.
const mobileColStyle: React.CSSProperties = {
  width: '50%',
  flex: '0 0 50%',
  maxWidth: '50%',
  paddingLeft: '8px',
  paddingRight: '8px',
};

/* ================= IMAGE WITH PLACEHOLDER ================= */
interface CategoryImageProps {
  src?: string;
  alt: string;
}

const CategoryImageWithPlaceholder: React.FC<CategoryImageProps> = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  const imageSrc = errored || !src
    ? 'asset/images/product-placeholder-landscape.png'
    : src;

  return (
    <div className="cat-img-wrap">
      {!loaded && (
        <img
          src="asset/images/product-placeholder-landscape.png"
          alt="placeholder"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover'
          }}
        />
      )}
      <img
        src={imageSrc}
        alt={alt}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: loaded ? 'block' : 'none'
        }}
        onLoad={() => setLoaded(true)}
        onError={() => {
          setErrored(true);
          setLoaded(true);
        }}
      />
      <div className="cat-overlay-shimmer" />
    </div>
  );
};

/* ================= MAIN COMPONENT ================= */
const CategoryBanner: React.FC = () => {
  const { bannerCategories, loading, error } = useCategories();
  const navigate = useNavigate();
  const rowRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  /* ── Intersection Observer: desktop only ── */
  useEffect(() => {
    if (!rowRef.current) return;
    if (window.innerWidth <= 768) return;

    const cards = rowRef.current.querySelectorAll<HTMLElement>('.cat-card');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('cat-card--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    cards.forEach((card) => observer.observe(card));
    return () => observer.disconnect();
  }, [bannerCategories]);

  const handleCategoryClick = (categoryId: string) => {
    navigate(`/shop?category=${encodeURIComponent(categoryId)}`);
  };

  if (loading) {
    return (
      <div className="sec-banner bg0 p-t-80 p-b-50">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading categories...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sec-banner bg0 p-t-80 p-b-50">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p className="text-danger">Error loading categories: {error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (bannerCategories.length === 0) {
    return (
      <div className="sec-banner bg0 p-t-80 p-b-50">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center">
              <p>No categories available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{categoryAnimationStyles}</style>

      <div className="sec-banner bg0 p-t-50 p-b-50">
        <div className="container">
          <div className="row" ref={rowRef}>
            {bannerCategories.map((category) => (
              <div
                key={category.id}
                className="col-sm-6 col-md-6 col-xl-4 p-b-30 cat-card"
                // Force 2-column layout on mobile via inline style — guarantees
                // it works regardless of Bootstrap version or theme overrides
                style={isMobile ? mobileColStyle : undefined}
              >
                <div className="block1 wrap-pic-w">
                  <CategoryImageWithPlaceholder
                    src={category.imageUrl}
                    alt={category.name}
                  />

                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleCategoryClick(category.id!);
                    }}
                    className="block1-txt ab-t-l s-full flex-col-l-sb p-lr-38 p-tb-34 trans-03 respon3"
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="block1-txt-child1 flex-col-l">
                      <span className="block1-name ltext-102 trans-04 p-b-8">
                        {category.name}
                      </span>
                    </div>

                    <div className="block1-txt-child2 p-b-4 trans-05">
                      <div className="block1-link stext-101 cl0 trans-09">
                        Shop Now
                      </div>
                    </div>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default CategoryBanner;